import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  TextField,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ImageIcon from '@mui/icons-material/Image';
import BackButton from '../../components/BackButton';
import { toast } from 'react-toastify';
import {
  fetchProductById,
  updateProduct,
  deleteProduct,
  uploadProductImage,
} from '../../controllers/paymentsController';
import { resolveProductImageUrl } from '../../api/httpClientPayments';
import {
  normalizeLogoIncomeArpCode,
  resolveEffectiveLogoIncomeArpCode,
} from './productFormUtils';
import { getCurrencyDisplayLabel, normalizeCurrencyInput } from '../../utils/currencyDisplay';

const buildFormFromProduct = (data) => ({
  name: data.name || '',
  description: data.description || '',
  category: data.category || '',
  price: String(data.price ?? ''),
  currency: data.currency || 'TRY',
  stockQuantity: data.stockQuantity != null ? String(data.stockQuantity) : '',
  maxQuantityPerOrder: data.maxQuantityPerOrder != null ? String(data.maxQuantityPerOrder) : '',
  imageFile: null,
  isActive: data.isActive,
  logoIncomeArpCode: data.logoIncomeArpCode ?? data.defaultLogoIncomeArpCode ?? '',
});

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    category: '',
    price: '',
    currency: 'TRY',
    stockQuantity: '',
    maxQuantityPerOrder: '',
    imageFile: null,
    isActive: true,
    logoIncomeArpCode: '',
  });

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const data = await fetchProductById(id);
        if (!active) return;
        setProduct(data);
        setForm(buildFormFromProduct(data));
        if (data.imageUrl) {
          setImagePreviewUrl(resolveProductImageUrl(data.imageUrl));
        }
      } catch (e) {
        if (!active) return;
        setError(e?.message || 'Ürün detayı alınamadı');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  const handleChange = (key) => (e) => {
    const value = key === 'isActive' ? e.target.value === 'true' : e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (imagePreviewUrl && imagePreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreviewUrl);
      setImagePreviewUrl(null);
    }
    if (file) {
      setImagePreviewUrl(URL.createObjectURL(file));
      setForm((prev) => ({ ...prev, imageFile: file }));
    } else {
      setForm((prev) => ({ ...prev, imageFile: null }));
    }
  };

  const handleClearImage = () => {
    if (imagePreviewUrl && imagePreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreviewUrl);
      setImagePreviewUrl(null);
    }
    setForm((prev) => ({ ...prev, imageFile: null }));
  };

  const handleSave = async () => {
    if (!form.name || !form.price) {
      toast.error('İsim ve fiyat zorunludur.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        id: product.id,
        name: form.name.trim(),
        description: form.description?.trim() || null,
        category: form.category?.trim() || null,
        price: Number(form.price),
        currency: form.currency || 'TRY',
        stockQuantity: form.stockQuantity !== '' ? Number(form.stockQuantity) : null,
        maxQuantityPerOrder:
          form.maxQuantityPerOrder !== '' ? Number(form.maxQuantityPerOrder) : null,
        isActive: form.isActive,
        logoIncomeArpCode: normalizeLogoIncomeArpCode(form.logoIncomeArpCode),
      };
      await updateProduct(payload);
      
      // If image file is selected, upload it separately
      if (form.imageFile) {
        await uploadProductImage({ productId: product.id, file: form.imageFile });
      }
      
      toast.success('Ürün güncellendi');
      setEditOpen(false);
      // Reload product data
      const updated = await fetchProductById(id);
      setProduct(updated);
      setForm(buildFormFromProduct(updated));
      if (updated.imageUrl) {
        setImagePreviewUrl(resolveProductImageUrl(updated.imageUrl));
      } else {
        setImagePreviewUrl(null);
      }
    } catch (e) {
      toast.error(e?.message || 'Ürün güncellenemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`${product.name} ürününü silmek istediğinize emin misiniz?`)) return;
    try {
      await deleteProduct(product.id);
      toast.success('Ürün silindi');
      navigate('/payments/misc');
    } catch (e) {
      toast.error(e?.message || 'Ürün silinemedi');
    }
  };

  const handleCloseDialog = () => {
    if (saving) return;
    if (imagePreviewUrl && imagePreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreviewUrl);
      setImagePreviewUrl(null);
    }
    // Reset form to product data
    if (product) {
      setForm(buildFormFromProduct(product));
      if (product.imageUrl) {
        setImagePreviewUrl(resolveProductImageUrl(product.imageUrl));
      } else {
        setImagePreviewUrl(null);
      }
    }
    setEditOpen(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <BackButton />
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      </Box>
    );
  }

  if (!product) {
    return null;
  }

  const effectiveLogoIncomeArpCode = product.effectiveLogoIncomeArpCode
    || resolveEffectiveLogoIncomeArpCode(product.logoIncomeArpCode, product.defaultLogoIncomeArpCode);

  const editEffectiveLogoIncomeArpCode = resolveEffectiveLogoIncomeArpCode(
    form.logoIncomeArpCode,
    product.defaultLogoIncomeArpCode,
  );

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <BackButton />
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="small"
            onClick={() => setEditOpen(true)}
            startIcon={<EditIcon sx={{ width: 16 }} />}
            sx={{
              textTransform: 'none',
              backgroundColor: '#E7EDF4',
              boxShadow: 'none',
              fontSize: 13,
              borderRadius: '5px',
              color: '#0E4D94',
              '&:hover': {
                backgroundColor: '#CFDBEA',
                boxShadow: 'none',
              },
            }}
          >
            Düzenle
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleDelete}
            startIcon={<DeleteOutlineIcon sx={{ width: 16 }} />}
            sx={{
              textTransform: 'none',
              backgroundColor: '#FFEEF3',
              boxShadow: 'none',
              fontSize: 13,
              borderRadius: '5px',
              color: '#F8285A',
              '&:hover': {
                backgroundColor: '#FFD6E0',
                boxShadow: 'none',
              },
            }}
          >
            Sil
          </Button>
        </Stack>
      </Stack>

      <Card sx={{ border: '1px solid #e7e7e7', boxShadow: 'none', borderRadius: '8px' }}>
        <CardContent>
          <Stack spacing={3}>
            {/* Ürün Görseli ve Temel Bilgiler */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '300px 1fr' },
                gap: 3,
                alignItems: 'flex-start',
              }}
            >
              {/* Görsel */}
              <Box>
                <Box
                  sx={{
                    width: '100%',
                    bgcolor: '#fafafa',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '1px solid #e6e6e6',
                    aspectRatio: '1',
                  }}
                >
                  {product.imageUrl ? (
                    <Box
                      component="img"
                      src={resolveProductImageUrl(product.imageUrl)}
                      alt={product.name}
                      sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    <ImageIcon sx={{ fontSize: 48, color: '#a3a3a3' }} />
                  )}
                </Box>
              </Box>

              {/* Temel Bilgiler */}
              <Box>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" sx={{ color: '#a3a3a3', fontSize: 12, mb: 0.5 }}>
                      Ürün Adı
                    </Typography>
                    <Typography sx={{ fontSize: 20, fontWeight: 600, color: '#444444' }}>
                      {product.name}
                    </Typography>
                  </Box>

                  {product.sku && (
                    <Box>
                      <Typography variant="body2" sx={{ color: '#a3a3a3', fontSize: 12, mb: 0.5 }}>
                        SKU
                      </Typography>
                      <Typography sx={{ fontSize: 14, color: '#444444' }}>{product.sku}</Typography>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="body2" sx={{ color: '#a3a3a3', fontSize: 12, mb: 0.5 }}>
                      Durum
                    </Typography>
                    <Chip
                      size="small"
                      label={product.isActive ? 'Aktif' : 'Pasif'}
                      sx={{
                        backgroundColor: product.isActive ? '#DFFFEA' : '#FFEEF3',
                        color: product.isActive ? '#17C653' : '#F8285A',
                        fontSize: 11,
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </Stack>
              </Box>
            </Box>

            <Divider />

            {/* Detaylı Bilgiler */}
            <Box>
              <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 600, color: '#444444', mb: 2 }}>
                Detaylı Bilgiler
              </Typography>
              <Stack spacing={2}>
                {product.description && (
                  <Box>
                    <Typography variant="body2" sx={{ color: '#a3a3a3', fontSize: 12, mb: 0.5 }}>
                      Açıklama
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: '#444444' }}>{product.description}</Typography>
                  </Box>
                )}

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 2,
                  }}
                >
                  {product.category && (
                    <Box>
                      <Typography variant="body2" sx={{ color: '#a3a3a3', fontSize: 12, mb: 0.5 }}>
                        Kategori
                      </Typography>
                      <Typography sx={{ fontSize: 14, color: '#444444' }}>{product.category}</Typography>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="body2" sx={{ color: '#a3a3a3', fontSize: 12, mb: 0.5 }}>
                      Fiyat
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: '#444444', fontWeight: 500 }}>
                      {product.price.toLocaleString('tr-TR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{' '}
                      {getCurrencyDisplayLabel(product.currency)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ color: '#a3a3a3', fontSize: 12, mb: 0.5 }}>
                      Stok Miktarı
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: '#444444' }}>
                      {product.stockQuantity != null ? product.stockQuantity : '-'}
                    </Typography>
                  </Box>

                  {product.maxQuantityPerOrder != null && (
                    <Box>
                      <Typography variant="body2" sx={{ color: '#a3a3a3', fontSize: 12, mb: 0.5 }}>
                        Sipariş Başına Max Adet
                      </Typography>
                      <Typography sx={{ fontSize: 14, color: '#444444' }}>
                        {product.maxQuantityPerOrder}
                      </Typography>
                    </Box>
                  )}

                  {product.order != null && (
                    <Box>
                      <Typography variant="body2" sx={{ color: '#a3a3a3', fontSize: 12, mb: 0.5 }}>
                        Sıra
                      </Typography>
                      <Typography sx={{ fontSize: 14, color: '#444444' }}>{product.order}</Typography>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="body2" sx={{ color: '#a3a3a3', fontSize: 12, mb: 0.5 }}>
                      Logo Gelir Hesabı
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: '#444444', fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}>
                      {effectiveLogoIncomeArpCode}
                    </Typography>
                    {!product.logoIncomeArpCode && product.defaultLogoIncomeArpCode && (
                      <Typography sx={{ fontSize: 12, color: '#a3a3a3', mt: 0.5 }}>
                        Sistem varsayılanı kullanılıyor
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>Ürünü Düzenle</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Ürün Adı"
              value={form.name}
              onChange={handleChange('name')}
              fullWidth
              required
            />
            <TextField
              label="Açıklama"
              value={form.description}
              onChange={handleChange('description')}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label="Kategori"
              value={form.category}
              onChange={handleChange('category')}
              fullWidth
            />
            <TextField
              label="Logo Gelir Hesabı"
              value={form.logoIncomeArpCode}
              onChange={handleChange('logoIncomeArpCode')}
              fullWidth
              placeholder="603.003.110"
              inputProps={{ maxLength: 50 }}
              helperText={`Kullanılan: ${editEffectiveLogoIncomeArpCode}. Boş bırakılırsa sistem varsayılanı kullanılır.`}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Fiyat"
                type="number"
                value={form.price}
                onChange={handleChange('price')}
                fullWidth
                required
                inputProps={{ min: 0, step: '0.01' }}
              />
              <TextField
              label="Para Birimi"
              value={getCurrencyDisplayLabel(form.currency || 'TRY')}
              onChange={(e) => setForm((prev) => ({
                ...prev,
                currency: normalizeCurrencyInput(e.target.value) || 'TRY',
              }))}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Stok Miktarı"
                type="number"
                value={form.stockQuantity}
                onChange={handleChange('stockQuantity')}
                fullWidth
              />
              <TextField
                label="Sipariş Başına Max Adet"
                type="number"
                value={form.maxQuantityPerOrder}
                onChange={handleChange('maxQuantityPerOrder')}
                fullWidth
              />
            </Stack>
            <Box sx={{ width: '100%' }}>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                Ürün Görseli
              </Typography>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                <Button variant="outlined" component="label" size="small" sx={{ textTransform: 'none' }}>
                  Dosya Seç
                  <input type="file" accept="image/*" hidden onChange={handleFileChange} />
                </Button>
                {form.imageFile && (
                  <Typography variant="body2" color="text.secondary">
                    {form.imageFile.name}
                  </Typography>
                )}
              </Stack>
              {imagePreviewUrl && (
                <Box sx={{ mt: 2 }}>
                  <Box
                    component="img"
                    src={imagePreviewUrl}
                    alt="Önizleme"
                    sx={{
                      display: 'block',
                      maxHeight: 180,
                      maxWidth: '100%',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      objectFit: 'contain',
                    }}
                  />
                  <Button size="small" onClick={handleClearImage} sx={{ mt: 1, textTransform: 'none' }}>
                    Görseli Kaldır
                  </Button>
                </Box>
              )}
            </Box>
            <TextField
              label="Durum"
              select
              value={String(form.isActive)}
              onChange={handleChange('isActive')}
              fullWidth
            >
              <MenuItem value="true">Aktif</MenuItem>
              <MenuItem value="false">Pasif</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            İptal
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductDetail;
